import { useRef } from 'react';
import {
    Image,
    Avatar,
    useStyles,
    useTheme,
    Carousel,
    pxToRem,
} from '@archipro-design/aria';
import { Link } from '@remix-run/react';
import * as S from './BannerAndLogo.style';
import { useCurrentTabBannerImage } from '../../../hook/use-current-tab-banner-image';
import {
    MEDIA_CONIDTION_SIZES_LAPTOP_ONLY,
    MEDIA_CONIDTION_SIZES_LARGE_SCREEN,
} from '~/modules/images/config/sizes-constant';
import type { ProfessionalBannersData } from '@modules/professional/page/OverviewPage';
import type { AwardProps } from '@modules/page-builder/component/detail/banner-awards/types';
import BannerAwards from '@modules/page-builder/component/detail/banner-awards/BannerAwards';
import { useFeature } from '@modules/root';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { HDEExhibitorBadge } from '@modules/hde/components/HDEExhibitorBadge';
import { HDE_PAGE_PATH } from '@modules/hde/constants';

interface BannerAndLogoMobileProps {
    companyLogoURI?: string | null;
    logoBackgroundColor?: string | null;
    banners: ProfessionalBannersData;
    awards?: AwardProps[];
    title: string;
    showHDEExhibitorMark?: boolean;
}

export const BannerAndLogoMobile = ({
    companyLogoURI = '',
    logoBackgroundColor = 'white',
    banners,
    awards,
    title,
    showHDEExhibitorMark = false,
}: BannerAndLogoMobileProps) => {
    const { css } = useStyles();
    const theme = useTheme();
    const bannerImage = useCurrentTabBannerImage(banners);
    const { Link: professionalLink } = useProfessionalInitialRouteData();
    const ref = useRef(null);
    const enableAwardBadges = useFeature('award_badges');

    const delay = 4000;

    return (
        <div className={css(S.Container)}>
            <div className={css(S.BannerWrap)}>
                {banners.length > 1 ? (
                    <Carousel
                        ref={ref}
                        aria-roledescription={'carousel'}
                        items={banners.map((img, key) => {
                            const src =
                                img.libraryImageFile?.DetailBannerStandard
                                    ?.Mobile;
                            if (src === null || src === undefined) return null;
                            return (
                                <Image
                                    alt={
                                        key === 0
                                            ? `Hero Banner for ${title}`
                                            : `Supporting Image ${key} for ${title}`
                                    }
                                    key={`bannerImgCarousel${key}`}
                                    src={src}
                                    layout={`fill`}
                                    objectFit={`cover`}
                                    loading={key === 0 ? 'eager' : 'lazy'}
                                    sizes={`${MEDIA_CONIDTION_SIZES_LARGE_SCREEN} 1920px, ${MEDIA_CONIDTION_SIZES_LAPTOP_ONLY} 95vw, 100vw`}
                                    width={414}
                                    height={297}
                                    overlay={{
                                        alwaysVisible: true,
                                        onClick: () => 0,
                                        icon: <></>,
                                        variables: {
                                            backgroundColor:
                                                theme.siteVariables.colors
                                                    .transparency.black['20%'],
                                        },
                                    }}
                                />
                            );
                        })}
                        autoplay={{
                            disableOnInteraction: false,
                            delay,
                        }}
                        navigation={true}
                        loop={true}
                        effect={'fade'}
                        fadeEffect={{
                            crossFade: true,
                        }}
                        hidePaddles={true}
                        design={{
                            top: 0,
                        }}
                        variables={{
                            overlayPadding: pxToRem(0),
                            overlayInitialOpacity: 1,
                            containerWidth: `100%`,
                            containerHeight: '100%',
                        }}
                    />
                ) : (
                    bannerImage && (
                        <Image
                            alt={`Hero Banner for ${title}`}
                            src={bannerImage}
                            width={414}
                            height={247}
                            layout={`fill`}
                            objectFit={`cover`}
                            loading="eager"
                            overlay={{
                                alwaysVisible: true,
                                onClick: () => 0,
                                icon: <></>,
                                variables: {
                                    backgroundColor:
                                        theme.siteVariables.colors.transparency
                                            .black['20%'],
                                },
                            }}
                        />
                    )
                )}
                {showHDEExhibitorMark && (
                    <div className="absolute right-4 top-18 z-[3]">
                        <Link
                            to={HDE_PAGE_PATH}
                            aria-label="View Home Design Event"
                            className="block transition-opacity hover:opacity-90"
                        >
                            <HDEExhibitorBadge className="h-auto w-24" />
                        </Link>
                    </div>
                )}
            </div>
            {enableAwardBadges && awards && awards.length > 0 && (
                <div className={css(S.BannerAwardsWrapper)}>
                    <BannerAwards
                        awards={awards}
                        heading={'Award winning professional'}
                        professionalUrl={professionalLink}
                    />
                </div>
            )}
            <div className={css(S.LogoWrap)}>
                <Avatar
                    image={{
                        src: companyLogoURI ?? '',
                        alt: `${title} company logo`,
                    }}
                    size={150}
                    imageFit={'contain'}
                    variables={{
                        avatarImageBackgroundColor:
                            logoBackgroundColor ?? undefined,
                        borderWidth: '0',
                        borderRadius: '0',
                        avatarBackgroundColor: 'unset',
                    }}
                />
            </div>
        </div>
    );
};
